// Auburn University: public college page (#2746), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Auburn's Common Data Set 2025-2026, published as web pages by
//    Institutional Research (filed under "2025"): section C, with C1
//    applicants, admits and enrollees for Fall 2025, C8 test policy for
//    Fall 2027, C14/C16 dates and C21/C22 plans. The C1 residency table is
//    not filled in. Quirk: C8 marks the SAT or ACT "Consider if Submitted"
//    for Fall 2027, but Admissions now requires scores for a complete
//    application; the page follows Admissions and says both.
//  - Auburn Admissions, Prospective Freshmen: the fall 2027 decision rounds,
//    the important dates table (December 1, 2026 and February 1, 2027 name
//    the cycle) and the standardized testing policy.
//  - Auburn Financial Aid, Estimated Cost of Attendance, 2026-2027 fall and
//    spring, undergraduate on campus (the CDS G1 gives 2025-2026 charges,
//    so it is not used).
//  - College Scorecard, school id 100858 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Auburn Common Data Set 2025-2026, section C',
        url: 'https://ir.auburn.edu/common-data-set/2025/section-c.php',
        read: '2026-10-01',
    },
    freshmen: {
        label: 'Auburn Admissions, Prospective Freshmen',
        url: 'https://www.auburn.edu/admissions/prospective-students/freshmen/index.php',
        read: '2026-10-01',
    },
    costs: {
        label: 'Auburn Financial Aid, Estimated Cost of Attendance 2026-2027',
        url: 'https://www.auburn.edu/administration/finaid/cost/estimates.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?100858-Auburn-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'auburn-university',
    kbId: 'auburn_university',
    name: 'Auburn University',
    shortName: 'Auburn',
    location: 'Auburn, AL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Auburn brand colors); decoration only.
    branding: {
        primary: '#0B2341',
        secondary: '#E86100',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/auburn_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 59121,
        admitted: 27724,
        enrolled: 6112,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Auburn\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Auburn has no early decision plan. Its early action is not binding and not restrictive. The Common Data Set gives no early action counts, so there is no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular decision', value: 'February 1, 2027' },
        rows: [
            { plan: 'Early Action, round 1', deadline: 'September 15', decision: 'Mid-October', binding: false },
            { plan: 'Early Action, round 2', deadline: 'October 15', decision: 'Mid-November', binding: false },
            { plan: 'Early Action, round 3', deadline: 'November 15', decision: 'Mid-December', binding: false },
            { plan: 'Early Action, round 4 (final)', deadline: 'December 1, 2026', decision: 'Early February', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1, 2027', decision: 'Early March', binding: false },
        ],
        note: 'Each date is when your complete file must be in: the application, the $50 fee or a fee waiver, your high school transcript and official ACT or SAT scores. For merit scholarships your file must be complete, scores included, by December 1, 2026; updated scores for more scholarships are due January 11, 2027. The application opens August 1 and the enrollment deposit is due May 1, 2027.',
        source: 'freshmen',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Auburn requires ACT or SAT scores on file for a complete application, sent officially by the testing agency. It accepts the ACT superscore report, builds an SAT superscore itself, and uses only your best result for admission and scholarships.',
        source: 'freshmen',
        cdsLine: 'The Common Data Set marks the SAT or ACT as considered if submitted for fall 2027; Auburn\'s admissions pages, which require scores, are the newer word.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshmen',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [13572, 37596] },
            { item: 'Housing (on campus)', amounts: [11966, 11966] },
            { item: 'Food', amounts: [6458, 6458] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1200, 1200] },
            { item: 'Personal', amounts: [3088, 3088] },
            { item: 'Transportation', amounts: [3232, 3232] },
        ],
        totals: [39516, 63540],
        note: 'For a full-time undergraduate living on campus, fall and spring. Some programs add a professional fee: Business, Engineering, Nursing clinical courses, Interior Design and Hospitality Management.',
        source: 'costs',
        netPrice: {
            average: 25424,
            byIncome: [
                { income: '$0 to $30,000', amount: 19536 },
                { income: '$30,001 to $48,000', amount: 19065 },
                { income: '$48,001 to $75,000', amount: 21665 },
                { income: '$75,001 to $110,000', amount: 25366 },
                { income: 'Over $110,000', amount: 30726 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Auburn\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
